import type {
  Assignment,
  ExerciseMode,
  Practitioner,
  SlotTemplate,
} from "@/api/types";

export type DayBlock = {
  startSlot: SlotTemplate;
  endSlot: SlotTemplate;
  practitionerId: string;
  practitionerLabel: string;
  modeCode: string | null;
  assignments: Assignment[];
};

export function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/**
 * For one day, build one block per (practitioner × contiguous slot range).
 * Adjacent slots with the same practitioner and matching end_time → start_time
 * are merged into a single block. Multiple practitioners on the same slot
 * become independent overlapping blocks.
 */
export function buildDayBlocks(
  daySlots: SlotTemplate[],
  assignmentsByKey: Map<string, Assignment[]>,
  date: string,
  practitionerById: Map<string, Practitioner>,
  modeById: Map<string, ExerciseMode>,
): DayBlock[] {
  const slots = [...daySlots].sort(
    (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time),
  );
  const out: DayBlock[] = [];
  const open = new Map<string, DayBlock>();

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]!;
    const list = assignmentsByKey.get(`${date}__${slot.id}`) ?? [];
    const presentNow = new Set(list.map((a) => a.practitioner_id));

    for (const [pid, blk] of Array.from(open.entries())) {
      const connectedInTime = blk.endSlot.end_time === slot.start_time;
      if (!presentNow.has(pid) || !connectedInTime) {
        out.push(blk);
        open.delete(pid);
      }
    }

    for (const a of list) {
      const pid = a.practitioner_id;
      const prac = practitionerById.get(pid);
      const mode = modeById.get(a.exercise_mode_id);
      const existing = open.get(pid);
      if (existing) {
        existing.endSlot = slot;
        existing.assignments.push(a);
        if (!existing.modeCode && mode) existing.modeCode = mode.code;
      } else {
        open.set(pid, {
          startSlot: slot,
          endSlot: slot,
          practitionerId: pid,
          practitionerLabel: prac?.display_name ?? pid.slice(0, 8),
          modeCode: mode?.code ?? null,
          assignments: [a],
        });
      }
    }
  }

  for (const blk of open.values()) out.push(blk);
  return out;
}


const PALETTE_LIGHT = [
  "#1976d2", "#7b1fa2", "#2e7d32", "#ef6c00", "#c2185b",
  "#0288d1", "#5d4037", "#00838f", "#827717", "#6a1b9a",
];
const PALETTE_DARK = [
  "#64b5f6", "#ce93d8", "#81c784", "#ffb74d", "#f48fb1",
  "#4fc3f7", "#bcaaa4", "#4dd0e1", "#dce775", "#ba68c8",
];

function paletteIndex(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

export function colorForPractitioner(id: string, mode: "light" | "dark"): string {
  const palette = mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT;
  return palette[paletteIndex(id) % palette.length]!;
}
